<template>
  <error-boundary error-key="roomCategories">
    <div class="mb-6 text-secondary paragraph-m_h">
      Добавить или удалить категории номеров можно перезапустив «Мастер вариантов размещения»
    </div>
    <e-data-items
      :model-value="selected"
      :items="items"
      :headers="headers"
      item-value="id"
      disabled
      :loading="isLoading"
    >
      <template v-slot:[`item.data-table-select`]="{ isSelected, internalItem, item }">
        <e-tooltip v-if="item.quotaStatus === QuotaStatus.QuotaInWork" :max-width="364">
          <template #activator>
            <e-icon name="lock" class="text-secondary d-block" />
          </template>
          <div class="text-secondary">
            <div class="paragraph-s font-600 mb-2">Квота в работе</div>
            <div class="paragraph-xs">Исключение категорий номера из Вариантов размещения запрещается</div>
          </div>
        </e-tooltip>
        <e-checkbox v-else :model-value="isSelected(internalItem)" disabled />
      </template>
      <template v-slot:[`item.name`]="{ item }">
        <text-with-badge v-if="item.quotaStatus === QuotaStatus.QuotaIsDraft">
          {{ item.name }}

          <template #tooltip>
            <div class="text-secondary">
              <b class="paragraph-s_h">Категории номеров, по которым нет продаж, но уже есть настройки квоты</b>
              <div class="paragraph-xs mt-2">После снятия выбора с такой категории номера эти настройки удаляются</div>
            </div>
          </template>
        </text-with-badge>

        <template v-else>
          {{ item.name }}
        </template>
      </template>
    </e-data-items>
  </error-boundary>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { DataTableHeaders } from '@/core/types/components/table';
import TextWithBadge from '@/core/components/common/TextWithBadge.vue';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import { ECheckbox, EIcon, ETooltip } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import { QuotaStatus } from '@/graphql/generated';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import { useRoomCategoriesStore } from '@/parts/room-categories/store/roomCategories';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const { activeTariff } = useTariffsStore();
const { getRoomCategories, roomCategories } = useRoomCategoriesStore();

const items = computed(
  () =>
    roomCategories.value.roomCategories.data?.map((roomCategory) => ({
      id: roomCategory.cid,
      name: roomCategory.name[0].value,
      type: roomCategory.type.name,
      base: roomCategory.seat.baseQuantity,
      ext: roomCategory.seat.extQuantity,
      isActive: roomCategory.isActive,
      quotaStatus: activeTariff.value?.baseSettings?.roomCategories?.find((r) => r.roomCategoryCid === roomCategory.cid)
        ?.quotaStatus,
    })) || [],
);

const selected = computed(() =>
  items.value
    ?.filter((item) => activeTariff.value?.baseSettings?.roomCategories.some((r) => r.roomCategoryCid === item.id))
    ?.map((item) => item.id),
);

const headers: DataTableHeaders = [
  {
    key: 'name',
    title: 'Категория номера',
  },
  {
    key: 'type',
    title: 'Тип',
    sortable: false,
    align: 'start',
    width: 250,
  },
  {
    key: 'base',
    title: 'Осн.',
    sortable: false,
    align: 'start',
    width: 80,
  },
  {
    key: 'ext',
    title: 'Доп.',
    sortable: false,
    align: 'start',
    width: 80,
  },
];

const { isLoading } = useFetch(async () => {
  await getRoomCategories();
});
</script>

<style scoped lang="scss"></style>
